import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import {
  AppActionItem,
  ApplicationDTO,
  HekaAccountMenu,
  HekaNavigationContainerComponent
} from 'ngx-heka-design-lib';
import {environment} from '../../../environments/local/environment';
import {Subscription} from 'rxjs';
import {LoadingService} from '../../services/loading/loading.service';
import {AccountMenuService} from '../../services/account-menu.service';
import {MultiDomainMgmtService} from '../../services/multi-domain-mgmt.service';
import {UserMgmtService} from '../../services/user-mgmt.service';
import {TranslatePipe} from '@ngx-translate/core';
import {ApplicationService} from '../../services/application.service';
import {DomainHandlerService} from '../../services/domain-handler-service';
import {L1L3AuthService} from 'ngx-auth-util-lib';
import {MatomoConfigService} from '../../services/matomo-config.service';
import {MatomoTracker} from 'ngx-matomo-client';
import {AsyncPipe} from '@angular/common';

@Component({
  selector: 'ngx-l2-multi-org-menu',
  imports: [
    HekaNavigationContainerComponent,
    TranslatePipe,
    AsyncPipe
  ],
  templateUrl: './l2-multi-org-menu.component.html',
  standalone: true,
  styleUrl: './l2-multi-org-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class L2MultiOrgMenuComponent
  implements OnInit, AfterViewInit, OnDestroy {

  readonly helpLink = environment.links.help;
  readonly hcpLink = environment.links.applications.hcp;

  readonly appMenuItems = signal<AppActionItem[]>([]);
  readonly accountMenu = signal<HekaAccountMenu | undefined>(undefined);

  readonly applications = signal<ApplicationDTO[]>([]);
  readonly isL3Access = signal(false);

  private readonly subscriptions = new Subscription();
  private profileSubscription?: Subscription;

  private helpLinkButton: HTMLElement | null = null;

  private readonly helpLinkClickHandler = (event: Event): void => {
    this.onHelpLinkClick(event);
  };

  constructor(
    public loadingService: LoadingService,
    public accountMenuService: AccountMenuService,
    private multiDomainMgmtService: MultiDomainMgmtService,
    private userMgmtService: UserMgmtService,
    private applicationService: ApplicationService,
    public domainHandlerService: DomainHandlerService,
    public authService: L1L3AuthService,
    public matomoConfigService: MatomoConfigService,
    public matomoTracker: MatomoTracker
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.multiDomainMgmtService.getSharedMultiDomainProfile(true)
        .subscribe({
          next: profile => {
            if (profile) {
              this.accountMenu.set(
                this.accountMenuService.buildAccountMenu(profile)
              );
            }
          }
        })
    );

    this.subscriptions.add(
      this.userMgmtService.userInfoChanged.subscribe({
        next: userInfo => {
          if (userInfo) {
            this.isL3Access.set(!!this.authService.getL3Token());

            if (this.isL3Access()) {
              this.getProfile();
            }
          }

          this.multiDomainMgmtService.updateMultiDomainProfile();
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.helpLinkButton = document.getElementById('sel-menu-help-btn');

    this.helpLinkButton?.addEventListener(
      'click',
      this.helpLinkClickHandler
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.profileSubscription?.unsubscribe();

    this.helpLinkButton?.removeEventListener(
      'click',
      this.helpLinkClickHandler
    );
  }

  onHelpLinkClick(event: Event): void {
    this.matomoTracker.trackLink(this.helpLink, 'link');
  }

  readonly openHcp = (): void => {
    window.location.href = this.hcpLink;
  };

  private setupApplicationsMenu(): void {
    const appMenuItems = this.applicationService.generateApplicationMenu(
      this.applications()
    );

    this.appMenuItems.set(appMenuItems);

    if (appMenuItems.findIndex(item => item.config.isCurrentApp) < 0) {
      this.authService.logout();
    }
  }

  private getProfile(): void {
    this.loadingService.setLoading(true);
    this.profileSubscription?.unsubscribe();

    this.profileSubscription = this.userMgmtService.getSharedProfile()
      .subscribe({
        next: profile => {
          if (profile) {
            this.applications.set(
              profile.applications?.length > 0
                ? profile.applications
                : []
            );

            this.setupApplicationsMenu();
          }

          this.loadingService.setLoading(false);
        },
        error: err => {
          this.loadingService.setLoading(false);
          throw err;
        }
      });
  }
}